import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ThumbsUp, ThumbsDown, ArrowLeft, ArrowRight, Reply, X, MessageCircle, List, Flag } from 'lucide-react';
import { queryKeys } from '@/api/queryKeys';
import { get, post, del } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { AdminBadge } from '@/components/atoms/AdminBadge';
import { Linkify } from '@/components/atoms/Linkify';
import { Button } from '@/components/atoms/Button';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { timeAgo } from '@/lib/format';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { FollowToggle } from '@/components/molecules/FollowToggle';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { ReportModal, type ReportTarget } from '@/components/organisms/ReportModal';
import { ImageLightbox } from '@/components/organisms/ImageLightbox';
import { ShareButton } from '@/components/molecules/ShareButton';
import { sanitizePostHtml, sanitizeGuidancePostHtml, renderMarkdownLite } from '@/lib/sanitize';
import { extractYouTubeUrls, getYouTubeId } from '@/lib/youtube';
import { YouTubePreviewList } from '@/components/molecules/YouTubePreview';
import { confirmModal } from '@/store/confirmStore';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { toast } from '@/store/toastStore';
import { isSystemAccount } from '@/constants/systemAccount';
import { AdBanner } from '@/components/ads/AdBanner';
import { SEOHead } from '@/components/atoms/SEOHead';
import { JsonLd } from '@/components/atoms/JsonLd';

interface PostDetail {
  id: number;
  title: string;
  content: string;
  post_type: string;
  ticker: string | null;
  view_count: number;
  like_count: number;
  dislike_count: number;
  comment_count: number;
  author_nickname: string;
  author_level: number;
  author_image: string | null;
  author_admin_role: string | null;
  author_is_test?: boolean | null;
  user_id: string | null;
  board_name: string;
  board_slug: string;
  /** 시스템 자동 게시글 출처 — market_kr_close / market_us_close / market_coin_24h / market_morning_brief / dart / rss_ai 등 */
  auto_source?: string | null;
  /** DART 원본 URL (auto_source==='dart' 인 경우만 — 공시 분석 버튼 활성화에 사용) */
  source_url?: string | null;
  /** 공시 중요도 — CRITICAL('중요', 자동 분석) / IMPORTANT('참고', 요청 시 분석) / NORMAL('일반', 미지원) */
  importance?: 'CRITICAL' | 'IMPORTANT' | 'NORMAL' | 'SKIP' | null;
  /** 공시 분석 레코드 출처 — 'pending-request' 면 '참고' 공시의 분석 요청 대기 상태 */
  analysis_source?: string | null;
  /** AI 분석 완료 여부 (judgment 채워짐) */
  analyzed?: boolean | null;
  is_pinned: boolean;
  is_anonymous?: boolean;
  created_at: string;
  my_vote: number | null;
  images?: { id: number; image_url: string; order_num: number }[];
}

interface Comment {
  id: number;
  parent_id: number | null;
  depth: number;
  content: string;
  like_count: number;
  dislike_count: number;
  is_blinded: boolean;
  is_edited: boolean;
  author_nickname: string;
  author_level: number;
  author_image: string | null;
  author_is_test?: boolean | null;
  parent_nickname: string | null;
  created_at: string;
  /** 내 투표 상태 — 1: 추천, -1: 비추천, null: 미투표 */
  my_vote: number | null;
  /** 소유권 판정용 (본인 댓글 투표 금지) */
  user_id?: string;
}

/** 시장상황 글의 auto_source 를 사용자 노출 카테고리 라벨로 매핑.
 *  market_reports.category 와 정합 — 5종(모닝브리핑/코인 24H/장중 브리핑/국장마감/미장마감). */
function marketCategoryLabel(autoSource?: string | null): string | null {
  switch (autoSource) {
    case 'market_morning_brief': return '모닝브리핑';
    case 'market_coin_24h':      return '코인 24H';
    case 'market_intraday':      return '장중 브리핑';
    case 'market_kr_close':      return '국장마감';
    case 'market_us_close':      return '미장마감';
    default:                     return null;
  }
}

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const postId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoggedIn, me } = useAuthStore();
  const { setLoginModalOpen } = useUIStore();

  // 최상위 댓글 입력
  const [commentText, setCommentText] = useState('');

  // 대댓글 입력 — 어느 댓글에 답글을 달고 있는지
  const [replyTarget, setReplyTarget] = useState<{ id: number; nickname: string } | null>(null);
  const [replyText, setReplyText] = useState('');

  // 신고 모달 상태
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleReport = (target: ReportTarget) => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    setReportTarget(target);
  };

  const postQuery = useQuery({
    queryKey: queryKeys.posts.detail(postId),
    queryFn: () => get<PostDetail>(`/posts/${postId}`),
    enabled: !!postId,
    // 404 는 재시도 의미 없음 — 즉시 에러 분기로 빠지도록 retry 비활성
    retry: (count, err) => {
      const status = (err as any)?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

  /* 존재하지 않는 게시물 — 404 시 NotFoundPage 렌더링 (아래 분기) */
  const isNotFound = (postQuery.error as any)?.response?.status === 404;

  /* ── '참고'(IMPORTANT) 공시 분석 요청 ─────────────────────────────────────
     '중요'(CRITICAL) 공시는 워커가 자동 분석하지만, '참고' 공시는 비용 절감을 위해
     첫 방문자가 요청할 때만 분석한다 (2026-09-17 정책). 한 번 분석되면 본문이
     분석 결과로 교체되어 이후 방문자는 그대로 열람한다. */
  const rceptNo = useMemo(() => {
    const m = (postQuery.data?.source_url ?? '').match(/rcpNo=(\d{14})/);
    return m ? m[1] : null;
  }, [postQuery.data?.source_url]);

  const analyzeMutation = useMutation({
    /* claude 분석은 20~60초 소요 — client 기본 timeout(15초) 대신 개별 지정 */
    mutationFn: () => post(`/disclosures/${rceptNo}/analyze`, {}, { timeout: 180_000 }),
    onSuccess: () => {
      toast.success('공시 분석이 완료됐어요.');
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
    },
    onError: (e: any) => {
      const status = e?.response?.status;
      /* 504/타임아웃 — 서버에서는 분석이 계속 진행돼 곧 본문에 반영된다 */
      if (status === 504 || e?.code === 'ECONNABORTED') {
        toast.error('분석이 오래 걸리고 있어요. 잠시 후 새로고침하면 결과를 볼 수 있어요.');
        return;
      }
      toast.error(
        e?.response?.data?.error?.message ?? '분석에 실패했어요. 잠시 후 다시 시도해주세요.',
      );
    },
  });

  const handleAnalyzeRequest = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (!rceptNo || analyzeMutation.isPending) return;
    analyzeMutation.mutate();
  };

  const rawContent = postQuery.data?.content ?? '';
  // 시스템 자동 생성 글 (guidance-bot 인포그래픽 / dart 공시 placeholder + 분석 HTML) 은 확장 sanitize 사용.
  // 신뢰 경계 — 시스템이 만든 자동글에만 div+style 화이트리스트 확장. 사용자 입력 (유저 글/댓글) 은 기존 sanitize 유지.
  const isSystemAutoPost = ['guidance-bot', 'dart'].includes(postQuery.data?.auto_source ?? '');
  // Markdown-lite 변환 후 sanitize — 공시/뉴스 자동글의 `\n\n`, `# `, `**bold**` 정상 렌더
  const safeContent = useMemo(
    () => isSystemAutoPost
      ? sanitizeGuidancePostHtml(rawContent)
      : sanitizePostHtml(renderMarkdownLite(rawContent)),
    [rawContent, isSystemAutoPost],
  );
  // 본문 내 YouTube 링크 추출 → 미리보기 카드 (썸네일+타이틀+채널명)
  const youtubeUrls = useMemo(() => extractYouTubeUrls(rawContent), [rawContent]);

  // 본문 이미지 클릭 → Lightbox (event delegation)
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      const src = (target as HTMLImageElement).getAttribute('src') ?? '';
      if (!src.startsWith('/uploads/')) return;
      const imgs = Array.from(root.querySelectorAll('img'))
        .map((el) => el.getAttribute('src') ?? '')
        .filter((s) => s.startsWith('/uploads/'));
      const idx = imgs.indexOf(src);
      if (idx < 0) return;
      e.preventDefault();
      setLightboxImages(imgs);
      setLightboxIndex(idx);
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [safeContent]);

  /* 본문 내 YouTube 링크(앵커) 제거 — 아래 미리보기 카드와 중복되므로 텍스트 링크는 숨김.
     링크만 있던 문단이 비면 그 문단도 함께 제거. */
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll('a').forEach((a) => {
      const href = a.getAttribute('href') ?? '';
      if (!getYouTubeId(href)) return;
      const parent = a.parentElement;
      a.remove();
      if (
        parent &&
        parent !== root &&
        (parent.textContent ?? '').trim() === '' &&
        parent.querySelectorAll('img, iframe').length === 0
      ) {
        parent.remove();
      }
    });
  }, [safeContent]);

  const commentsQuery = useQuery({
    queryKey: queryKeys.comments.byPost(postId),
    queryFn: () => get<{ comments: Comment[] }>(`/comments?postId=${postId}`),
    enabled: !!postId,
  });

  /** 이전글/다음글 — 같은 게시판에서 인접 id */
  const neighborsQuery = useQuery({
    queryKey: ['posts', 'neighbors', postId],
    queryFn: () =>
      get<{
        prev: { id: number; title: string } | null;
        next: { id: number; title: string } | null;
      }>(`/posts/${postId}/neighbors`),
    enabled: !!postId,
    staleTime: 30_000,
  });

  const voteMutation = useMutation({
    mutationFn: (type: 1 | -1) => post(`/posts/${postId}/vote`, { type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
      // 게시글 목록/인기글 모두 추천 수 반영
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.hot });
      queryClient.invalidateQueries({ queryKey: ['posts', 'popular'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '투표 처리에 실패했어요.');
    },
  });

  /** 댓글/대댓글 통합 등록 mutation */
  const commentMutation = useMutation({
    mutationFn: (params: { content: string; parentId?: number }) =>
      post('/comments', { postId, content: params.content, parentId: params.parentId }),
    onSuccess: () => {
      setCommentText('');
      setReplyText('');
      setReplyTarget(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPost(postId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '댓글 등록에 실패했어요.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => del(`/posts/${postId}`),
    onSuccess: () => {
      // 캐시 무효화: 목록/상세 모두 갱신 (삭제된 글이 목록에 남지 않도록)
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.removeQueries({ queryKey: queryKeys.posts.detail(postId) });
      navigate(-1);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '삭제에 실패했어요.');
    },
  });

  /** 댓글 추천/비추 mutation */
  const commentVoteMutation = useMutation({
    mutationFn: (params: { commentId: number; type: 1 | -1 }) =>
      post(`/comments/${params.commentId}/vote`, { type: params.type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.byPost(postId) });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '댓글 투표에 실패했어요.');
    },
  });

  const handleCommentVote = (comment: Comment, type: 1 | -1) => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (comment.user_id && me?.userId === comment.user_id) {
      toast.error('본인 댓글은 추천할 수 없어요.');
      return;
    }
    commentVoteMutation.mutate({ commentId: comment.id, type });
  };

  const handleVote = (type: 1 | -1) => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    voteMutation.mutate(type);
  };

  const handleCommentSubmit = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (commentText.trim().length === 0) return;
    commentMutation.mutate({ content: commentText });
  };

  const handleReplySubmit = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (replyText.trim().length === 0 || !replyTarget) return;
    commentMutation.mutate({ content: replyText, parentId: replyTarget.id });
  };

  const openReply = (comment: Comment) => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    setReplyTarget({ id: comment.id, nickname: comment.author_nickname });
    setReplyText('');
  };

  const cancelReply = () => {
    setReplyTarget(null);
    setReplyText('');
  };

  if (isNotFound) {
    return <NotFoundPage />;
  }

  if (postQuery.isLoading) {
    return (
      <div className="container-base py-6">
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const p = postQuery.data;
  if (!p) return null;

  const isMine = !!p.user_id && me?.userId === p.user_id;
  // 시세공간봇(시스템 계정) 작성글 — 쪽지/팔로우/신고 비노출
  const isBotAuthor = isSystemAccount(p.user_id);
  // 익명 글(건의/제안 13) — 프로필 클릭/쪽지/팔로우/신고 전부 비활성화
  const isAnonymousPost = !!p.is_anonymous || !p.user_id;

  // SEO 메타 — 본문 HTML 태그 제거 후 100자 잘라 description 사용
  const seoDescription = (() => {
    const stripped = String(p.content ?? '')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    if (!stripped) return undefined;
    if (stripped.length <= 100) return stripped;
    // 100자 잘림 지점이 이모지(서로게이트 페어) 중간이면 반쪽 제거 — JSON-LD 깨진 유니코드 방지
    return stripped.slice(0, 100).replace(/[\uD800-\uDBFF]$/, '') + '…';
  })();

  // 첫 번째 첨부 이미지 → og:image (없으면 SEOHead 의 기본 OG 이미지 fallback)
  const firstImage = (p.images ?? []).slice().sort((a, b) => a.order_num - b.order_num)[0]
    ?.image_url;

  // JSON-LD 페이로드 — Article + BreadcrumbList
  const SITE_URL = 'https://community.insidemation.com';
  const postUrl = `${SITE_URL}/post/${p.id}`;

  const articleSchema = {
    '@type': 'Article',
    headline: p.title,
    description: seoDescription,
    author: {
      '@type': 'Person',
      name: p.is_anonymous ? '익명' : p.author_nickname,
    },
    datePublished: p.created_at,
    dateModified: p.created_at,
    publisher: {
      '@type': 'Organization',
      name: '시세공간',
      logo: {
        '@type': 'ImageObject',
        // Google publisher.logo 가이드라인: 600×60 권장. 800×400 도 허용 범위.
        url: `${SITE_URL}/og_800_400.png`,
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
    ...(firstImage && {
      image: firstImage.startsWith('http') ? firstImage : `${SITE_URL}${firstImage}`,
    }),
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '홈', item: `${SITE_URL}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: p.board_name,
        item: `${SITE_URL}/board/${p.board_slug}`,
      },
      { '@type': 'ListItem', position: 3, name: p.title, item: postUrl },
    ],
  };

  return (
    <>
      <SEOHead
        title={p.title}
        description={seoDescription}
        canonical={`/post/${p.id}`}
        image={firstImage}
        type="article"
      />
      <JsonLd data={[articleSchema, breadcrumbSchema]} />
      <BoardTabs activeSlug={p.board_slug} />
      <div className="container-base pb-6">
      <article className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6">
        {/* 헤더 — 시장상황은 글의 성격에 맞춰 카테고리 라벨로 대체 */}
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="primary">
            {p.board_slug === 'market'
              ? marketCategoryLabel(p.auto_source) ?? p.board_name
              : p.board_name}
          </Badge>
        </div>
        <h1 className="mb-4 min-w-0 overflow-hidden break-words text-2xl font-extrabold leading-snug">{p.title}</h1>

        {/* 작성자 — 모바일: [프로필]/[버튼들] 2행 고정, sm+: 한 줄 정렬.
            가이던스 분석 게시판은 봇이 자동 작성하므로 작성자/날짜 영역 자체를 숨김. */}
        {p.board_slug !== 'guidance-analysis' && (
        <div className="mb-6 flex flex-col gap-3 border-b border-[var(--color-border)] pb-4 sm:flex-row sm:items-center sm:justify-between">
          {isAnonymousPost ? (
            <div className="flex min-w-0 items-center gap-3 overflow-hidden sm:flex-1">
              <Avatar src={null} alt={p.author_nickname} size="md" />
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                  <span className="min-w-0 truncate font-semibold">{p.author_nickname}</span>
                </div>
                <div className="truncate text-sm text-[var(--color-text-sub)]">
                  작성일 {timeAgo(p.created_at)} · 조회 {p.view_count}
                </div>
              </div>
            </div>
          ) : (
            <Link to={`/users/${p.user_id}`} className="flex min-w-0 items-center gap-3 overflow-hidden hover:opacity-80 sm:flex-1">
              <Avatar src={p.author_image ?? undefined} alt={p.author_nickname} size="md" />
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                  <span className="min-w-0 truncate font-semibold hover:underline">{p.author_nickname}</span>
                  <AdminBadge role={p.author_admin_role} />
                  {!isBotAuthor && <LevelBadge level={p.author_level} size="sm" />}
                </div>
                <div className="truncate text-sm text-[var(--color-text-sub)]">
                  작성일 {timeAgo(p.created_at)} · 조회 {p.view_count}
                </div>
              </div>
            </Link>
          )}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!isMine && !isBotAuthor && !isAnonymousPost && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="쪽지 보내기"
                  onClick={() => {
                    if (!isLoggedIn) return setLoginModalOpen(true);
                    navigate(
                      `/messages?to=${p.user_id}&nickname=${encodeURIComponent(p.author_nickname)}`,
                    );
                  }}
                  className="flex w-[68px] shrink-0 items-center justify-center gap-1 text-sm"
                >
                  <MessageCircle className="h-4 w-4" />
                  쪽지
                </Button>
                <FollowToggle userId={p.user_id!} />
              </>
            )}
            {isMine && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/write?edit=${p.id}`)}
                >
                  수정
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={deleteMutation.isPending}
                  onClick={async () => {
                    const ok = await confirmModal({
                      title: '게시글 삭제',
                      message: '정말 삭제할까요?\n삭제된 게시글은 복구할 수 없어요.',
                      confirmLabel: '삭제',
                      tone: 'danger',
                    });
                    if (ok) deleteMutation.mutate();
                  }}
                >
                  삭제
                </Button>
              </>
            )}
            {!isMine && !isBotAuthor && !isAnonymousPost && (
              <Button
                variant="ghost"
                size="sm"
                aria-label="신고"
                onClick={() => handleReport({ type: 'post', id: p.id })}
                className="flex w-[68px] shrink-0 items-center justify-center gap-1 text-sm"
              >
                <Flag className="h-4 w-4" />
                신고
              </Button>
            )}
          </div>
        </div>
        )}

        {/* '참고' 공시 분석 요청 버튼 — 요청 대기 상태(pending-request)일 때만 노출.
            분석이 끝나면 본문이 분석 결과로 교체되고 이 블록은 사라진다. */}
        {p.auto_source === 'dart' && p.analysis_source === 'pending-request' && !p.analyzed && (
          <div className="mb-5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-center">
            <p className="mb-3 text-sm text-[var(--color-text-sub)]">
              이 공시는 <b className="text-[var(--color-text)]">참고</b> 등급이라 자동 분석 대상이 아니에요.
              <br className="hidden sm:block" />
              분석을 요청하시면 AI가 공시 원문을 읽고 정리해 드려요. (20~60초 소요)
            </p>
            <Button
              size="md"
              loading={analyzeMutation.isPending}
              disabled={analyzeMutation.isPending}
              onClick={handleAnalyzeRequest}
              className="bg-[#62b058] text-white hover:bg-[#57a04e] active:bg-[#4e9045]"
            >
              {analyzeMutation.isPending ? 'AI가 공시를 읽고 있어요' : 'AI 분석 요청'}
            </Button>
          </div>
        )}

        {/* 본문 — DOMPurify 재필터링 후 렌더.
            공시 글(auto_source='dart')은 자동 분석 워커가 본문을 5필드 분석 HTML 로 교체하므로
            별도 분석 컴포넌트 없이 본문 자체가 분석 내용. 미분석 글은 placeholder 메시지. */}
        <div
          ref={contentRef}
          className="prose prose-sm max-w-none post-content"
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />

        {/* YouTube 미리보기 — 본문 내 YouTube 링크 자동 카드화 (에디터와 동일 표시) */}
        <YouTubePreviewList urls={youtubeUrls} />

        {/* 뉴스 게시판(AI 새 기사 전용) — 본문 하단 고정 안내 문구 */}
        {p.board_slug === 'news' && (
          <p className="mt-8 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-text-sub)]">
            본 기사는 AI가 작성한 콘텐츠입니다.
          </p>
        )}

        {/* 레거시 post_images (구버전 글 호환) — HTML 내 img 와 별개로 카드 형태 표시 */}
        {Array.isArray(p.images) && p.images.length > 0 && (
          <div className="mt-4 flex flex-col">
            {p.images.map((img, i) => (
              <button
                key={img.id}
                type="button"
                onClick={() => {
                  setLightboxImages(p.images!.map((x) => x.image_url));
                  setLightboxIndex(i);
                }}
                className="mb-3 block w-full bg-transparent p-0 last:mb-0"
              >
                <img
                  src={img.image_url}
                  alt={`첨부 ${i + 1}`}
                  loading="lazy"
                  className="block h-auto w-full md:w-auto"
                />
              </button>
            ))}
          </div>
        )}

        {/* 광고: 본문 아래 인아티클 (#5) — 페이지당 1개 노출 정책으로 제거 (2026-04-24).
            유일한 광고는 댓글 섹션 아래로 이관. */}

        {/* 투표 + 공유 — 모바일에서 원문 보기/본문과 충분한 간격 확보 */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:mt-6">
          <Button
            variant={p.my_vote === 1 ? 'primary' : 'secondary'}
            onClick={() => handleVote(1)}
            loading={voteMutation.isPending && voteMutation.variables === 1}
            disabled={isMine}
          >
            <ThumbsUp className="h-4 w-4" />
            추천 {p.like_count}
          </Button>
          <Button
            variant={p.my_vote === -1 ? 'danger' : 'secondary'}
            onClick={() => handleVote(-1)}
            loading={voteMutation.isPending && voteMutation.variables === -1}
            disabled={isMine}
          >
            <ThumbsDown className="h-4 w-4" />
            비추 {p.dislike_count}
          </Button>
          <ShareButton
            url={`${window.location.origin}/post/${p.id}`}
            title={p.title}
            description={
              (p.content ?? '')
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 100)
                // 잘림 끝 반쪽 이모지 제거 (깨진 유니코드 방지)
                .replace(/[\uD800-\uDBFF]$/, '')
            }
          />
        </div>
        {isMine && (
          <p className="mt-2 text-center text-xs text-[var(--color-text-sub)]">
            본인 게시글에는 추천할 수 없어요.
          </p>
        )}
      </article>

      {/* 댓글 */}
      <section className="mt-8 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <h2 className="mb-4 section-title">💬 댓글 {p.comment_count}개</h2>

        {/* 최상위 댓글 입력 */}
        <div className="mb-6">
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={isLoggedIn ? '댓글을 입력하세요...' : '로그인 후 작성 가능해요'}
            disabled={!isLoggedIn}
            maxLength={1000}
            rows={3}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40 disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-sub)]">
              {commentText.length}/1000
              {' · '}
              {commentText.trim().length >= 10
                ? '✓ 포인트 지급 조건'
                : '10자 이상 작성 시 포인트가 지급됩니다'}
            </span>
            <Button
              size="sm"
              onClick={handleCommentSubmit}
              loading={commentMutation.isPending && !replyTarget}
              disabled={commentText.trim().length === 0}
            >
              등록
            </Button>
          </div>
        </div>

        {/* 목록 */}
        <div className="space-y-4">
          {commentsQuery.data?.comments?.map((c) => (
            <div key={c.id}>
              <div
                className={`flex gap-3 ${
                  c.depth === 1
                    ? 'ml-4 border-l-2 border-[var(--color-border)] pl-3 sm:ml-8 sm:pl-4'
                    : ''
                }`}
              >
                <Avatar src={c.author_image ?? undefined} alt={c.author_nickname} size="sm" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold">{c.author_nickname}</span>
                    <LevelBadge level={c.author_level} size="sm" />
                    <span className="text-[var(--color-text-sub)]">{timeAgo(c.created_at)}</span>
                    {c.is_edited && (
                      <span className="text-[var(--color-text-sub)]">(수정됨)</span>
                    )}
                  </div>
                  {c.is_blinded ? (
                    <p className="mt-1 text-sm italic text-[var(--color-text-sub)]">
                      🚫 비추천이 많아 숨겨진 댓글이에요.
                    </p>
                  ) : (
                    <p className="mt-1 text-sm whitespace-pre-wrap">
                      {c.parent_nickname && (
                        <span className="mr-1 text-[var(--color-primary)]">
                          @{c.parent_nickname}
                        </span>
                      )}
                      <Linkify text={c.content} />
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-xs text-[var(--color-text-sub)]">
                    {/* 댓글 추천 */}
                    <button
                      onClick={() => handleCommentVote(c, 1)}
                      disabled={
                        c.is_blinded ||
                        (c.user_id !== undefined && me?.userId === c.user_id) ||
                        (commentVoteMutation.isPending &&
                          commentVoteMutation.variables?.commentId === c.id)
                      }
                      className={`inline-flex items-center gap-1 transition-colors ${
                        c.my_vote === 1
                          ? 'text-[var(--color-rise)] font-semibold'
                          : 'hover:text-[var(--color-rise)]'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      aria-label="추천"
                    >
                      👍 {c.like_count}
                    </button>
                    {/* 댓글 비추천 */}
                    <button
                      onClick={() => handleCommentVote(c, -1)}
                      disabled={
                        c.is_blinded ||
                        (c.user_id !== undefined && me?.userId === c.user_id) ||
                        (commentVoteMutation.isPending &&
                          commentVoteMutation.variables?.commentId === c.id)
                      }
                      className={`inline-flex items-center gap-1 transition-colors ${
                        c.my_vote === -1
                          ? 'text-[var(--color-fall)] font-semibold'
                          : 'hover:text-[var(--color-fall)]'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      aria-label="비추천"
                    >
                      👎 {c.dislike_count}
                    </button>
                    {/* 대댓글은 depth=0 댓글에만 달 수 있음 (2단계 제한) */}
                    {c.depth === 0 && !c.is_blinded && (
                      <button
                        onClick={() => openReply(c)}
                        className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
                      >
                        <Reply className="h-3 w-3" />
                        답글
                      </button>
                    )}
                    {c.user_id && c.user_id !== me?.userId && !c.is_blinded && (
                      <button
                        onClick={() => handleReport({ type: 'comment', id: c.id })}
                        className="inline-flex items-center gap-1 hover:text-[var(--color-rise)]"
                        aria-label="신고"
                      >
                        <Flag className="h-3 w-3" />
                        신고
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 인라인 답글 입력창 — replyTarget.id 와 일치하는 댓글 바로 아래에만 표시 */}
              {replyTarget?.id === c.id && (
                <div className="mt-2 ml-4 rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3 sm:ml-8">
                  <div className="mb-2 flex items-center justify-between text-xs">
                    <span className="text-[var(--color-primary)]">
                      @{replyTarget.nickname}님에게 답글
                    </span>
                    <button
                      onClick={cancelReply}
                      className="inline-flex items-center gap-1 text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
                      aria-label="답글 취소"
                    >
                      <X className="h-3 w-3" />
                      취소
                    </button>
                  </div>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="답글을 입력하세요..."
                    maxLength={1000}
                    rows={2}
                    autoFocus
                    className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-[var(--color-text-sub)]">
                      {replyText.length}/1000
                    </span>
                    <Button
                      size="sm"
                      onClick={handleReplySubmit}
                      loading={commentMutation.isPending && !!replyTarget}
                      disabled={replyText.trim().length === 0}
                    >
                      답글 등록
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {commentsQuery.data?.comments?.length === 0 && (
            <p className="py-8 text-center text-sm text-[var(--color-text-sub)]">
              첫 댓글을 남겨보세요!
            </p>
          )}
        </div>
      </section>

      {/* 광고: 댓글 섹션 아래 디스플레이 사각형 (#1) — 본문/댓글 위 광고 제거 후 이관 (2026-04-24) */}
      <AdBanner slot="display-rect" className="mt-3 sm:mt-6" />

      {/* 하단 네비게이션 — 좌: 목록으로 / 우: 이전글 · 다음글 */}
      <nav className="mt-6 flex items-center justify-between gap-2 border-t border-[var(--color-border)] pt-4">
        <button
          type="button"
          onClick={() => navigate(`/board/${p.board_slug}`)}
          className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg)]"
        >
          <List className="h-4 w-4" />
          목록으로
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!neighborsQuery.data?.prev}
            onClick={() =>
              neighborsQuery.data?.prev &&
              navigate(`/post/${neighborsQuery.data.prev.id}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
            title={neighborsQuery.data?.prev?.title}
          >
            <ArrowLeft className="h-4 w-4" />
            이전글
          </button>
          <button
            type="button"
            disabled={!neighborsQuery.data?.next}
            onClick={() =>
              neighborsQuery.data?.next &&
              navigate(`/post/${neighborsQuery.data.next.id}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
            title={neighborsQuery.data?.next?.title}
          >
            다음글
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </nav>
      </div>
      <ReportModal
        target={reportTarget}
        onClose={() => setReportTarget(null)}
      />
      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}

